import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from './supabase';

// Timing for the inactivity-based session timeout.
export const IDLE_LIMIT_MS = 5 * 60 * 1000; // sign out after 5 min of inactivity
export const WARNING_AT_MS  = 4 * 60 * 1000; // show the warning modal after 4 min
const TICK_MS = 1000;
const ACTIVITY_SYNC_THROTTLE_MS = 2000;

// localStorage is shared by every tab of the same origin, so it is used both to
// sync inactivity across tabs and to surface the "session expired" notice on the
// login screen (sessionStorage would not reach the other tabs).
const ACTIVITY_KEY = 'mm_last_activity';
export const EXPIRED_FLAG = 'mm_session_expired';

// Only genuine user input resets the timer. Background API calls, Supabase
// polling and automatic refreshes never fire these DOM events, so they cannot
// keep a session alive.
const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'click', 'keydown',
  'scroll', 'wheel', 'touchstart', 'touchmove', 'input',
];

// Thoroughly clear every trace of the authenticated session: Supabase tokens,
// any cached auth state in local/session storage, and cookies. The Supabase
// sign-out also propagates the sign-out to other tabs via its own storage sync.
export async function clearSessionEverywhere() {
  try { await supabase.auth.signOut(); } catch (e) { /* ignore */ }
  try {
    Object.keys(window.localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.includes('supabase') || key.includes('auth-token') || key === ACTIVITY_KEY) {
        window.localStorage.removeItem(key);
      }
    });
  } catch (e) { /* ignore */ }
  try { window.sessionStorage.clear(); } catch (e) { /* ignore */ }
  try {
    document.cookie.split(';').forEach(c => {
      const name = c.split('=')[0].trim();
      if (name) document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    });
  } catch (e) { /* ignore */ }
}

// ── Activity holds ──────────────────────────────────────────────────────────
// Some work keeps a user genuinely busy without producing any DOM events — an
// active phone call being the obvious one. A hold suppresses the idle timer
// until it is released. Counted rather than boolean so overlapping holds
// cannot release each other early.
let _holds = 0;

export function holdSession() {
  _holds += 1;
  let released = false;
  return function release() {
    if (released) return;
    released = true;
    _holds = Math.max(0, _holds - 1);
  };
}

export function sessionHeld() { return _holds > 0; }

// ── Extended idle limit ─────────────────────────────────────────────────────
// Softer than a hold: the session still ends after a long idle spell, just not
// after five minutes. Used while the dialer is registered, because an agent
// waiting for inbound calls produces no input events but must stay signed in
// to be rung. The longest active extension wins.
const _extensions = new Map();
let _extSeq = 0;

export function extendIdleLimit(ms) {
  const id = ++_extSeq;
  _extensions.set(id, ms);
  return function release() { _extensions.delete(id); };
}

export function currentIdleLimit() {
  let limit = IDLE_LIMIT_MS;
  for (const ms of _extensions.values()) if (ms > limit) limit = ms;
  return limit;
}

// Tracks user inactivity and drives the warning countdown. Calls onTimeout once
// the idle limit is reached. Activity is synchronised across tabs so an active
// user in any tab keeps every tab signed in.
export function useInactivityTimeout(onTimeout) {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.round((IDLE_LIMIT_MS - WARNING_AT_MS) / 1000));
  const lastActivity = useRef(Date.now());
  const lastSync = useRef(0);
  const firedRef = useRef(false);
  const warningRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const reset = useCallback((broadcast) => {
    const now = Date.now();
    lastActivity.current = now;
    firedRef.current = false;
    warningRef.current = false;
    setWarning(false);
    // Throttle cross-tab writes so a stream of mousemove events does not hammer
    // localStorage; one ping every couple of seconds is enough to stay in sync.
    if (broadcast && now - lastSync.current > ACTIVITY_SYNC_THROTTLE_MS) {
      lastSync.current = now;
      try { window.localStorage.setItem(ACTIVITY_KEY, String(now)); } catch (e) { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    // While the warning is on screen the passive activity listeners are ignored,
    // so the countdown is meaningful: the user must explicitly choose to stay.
    const onActivity = () => { if (!warningRef.current) reset(true); };
    ACTIVITY_EVENTS.forEach(ev =>
      window.addEventListener(ev, onActivity, { passive: true, capture: true }));

    // Activity in (or sign-out from) another tab keeps this tab in sync.
    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY && e.newValue) {
        const ts = Number(e.newValue);
        if (!Number.isNaN(ts)) {
          lastActivity.current = ts;
          firedRef.current = false;
          warningRef.current = false;
          setWarning(false);
        }
      }
    };
    window.addEventListener('storage', onStorage);

    const interval = setInterval(() => {
      // An active call counts as activity. Broadcast it so other tabs of the
      // same session stay alive too.
      if (sessionHeld()) { reset(true); return; }

      const idle = Date.now() - lastActivity.current;
      const limit = currentIdleLimit();
      // The warning always comes one minute (IDLE - WARNING) before the end.
      const warnAt = limit - (IDLE_LIMIT_MS - WARNING_AT_MS);
      if (idle >= limit) {
        if (!firedRef.current) {
          firedRef.current = true;
          if (onTimeoutRef.current) onTimeoutRef.current();
        }
        return;
      }
      if (idle >= warnAt) {
        warningRef.current = true;
        setWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((limit - idle) / 1000)));
      } else if (warningRef.current) {
        warningRef.current = false;
        setWarning(false);
      }
    }, TICK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach(ev =>
        window.removeEventListener(ev, onActivity, { capture: true }));
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [reset]);

  const staySignedIn = useCallback(() => reset(true), [reset]);
  return { warning, secondsLeft, staySignedIn };
}
